// v0.50.3 — contact nicknames. A per-device map of wallet address → a name the
// user chose ("Mum"). Nicknames are for OTHER people's wallets (contacts); they
// live only in this browser (localStorage), never on any server. Display order
// everywhere a wallet appears is: nickname → code → short 0x…. The underlying
// code/address is the payable identity; the nickname is just what the user sees
// and "dials", the way you never look at a saved phone number.

import { formatCode } from "./codes";

const KEY = "m.nick";

type NickMap = Record<string, string>;

function read(): NickMap {
  try {
    const raw = localStorage.getItem(KEY);
    const obj = raw ? (JSON.parse(raw) as unknown) : {};
    return obj && typeof obj === "object" ? (obj as NickMap) : {};
  } catch {
    return {};
  }
}
function write(map: NickMap) {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* best-effort */
  }
}

/** The saved nickname for an address, or null. Keyed case-insensitively. */
export function readNick(address: string): string | null {
  if (!address) return null;
  return read()[address.toLowerCase()] ?? null;
}
export function setNick(address: string, name: string): void {
  const map = read();
  const k = address.toLowerCase();
  const n = name.trim();
  if (n) map[k] = n;
  else delete map[k];
  write(map);
}
export function removeNick(address: string): void {
  const map = read();
  delete map[address.toLowerCase()];
  write(map);
}

/** All saved nicknames as {address, name} — for contact search + duplicate checks. */
export function listNicks(): { address: string; name: string }[] {
  const map = read();
  return Object.keys(map).map((address) => ({ address, name: map[address] }));
}

/** The address that already uses this name (any case), if any other than `self`. */
export function nameClash(name: string, self: string): string | null {
  const n = name.trim().toLowerCase();
  if (!n) return null;
  const self_ = self.toLowerCase();
  const hit = listNicks().find(
    (x) => x.name.toLowerCase() === n && x.address.toLowerCase() !== self_,
  );
  return hit ? hit.name : null;
}

import { cachedProfile } from "./profileCache";

const shortAddr = (a: string) =>
  a && a.startsWith("0x") ? `${a.slice(0, 6)}…${a.slice(-4)}` : a;

/** What to show for a wallet (v0.70.18): nickname → their own name → code → short 0x….
 *  A bare code is the last resort — it's the M-link slug, never an identity. */
export function contactLabel(address: string, code?: string | null): string {
  const theirs = cachedProfile(address);
  return readNick(address) || theirs?.name || code || theirs?.code || shortAddr(address) || "wallet";
}

// The pencil affordance (from Pencil.svg). fill=currentColor so CSS colours it.
export const PENCIL = `<svg viewBox="0 0 241.52 241.11" fill="currentColor" aria-hidden="true"><path d="M51.03,241.11c-16.36,0-28.95-4.42-37.78-13.25S0,206.44,0,190.08v-114.14c0-16.44,4.41-29.07,13.24-37.9,8.83-8.83,21.42-13.24,37.78-13.24l114.87.12c1.71,0,3.38.08,5,.24,1.63.16,3.17.37,4.64.61l-19.04,19.29c-1.38-.16-2.81-.31-4.27-.43s-2.93-.18-4.39-.18H51.03c-10.01,0-17.74,2.73-23.19,8.18s-8.18,13.18-8.18,23.19v114.26c0,10.09,2.73,17.84,8.18,23.25,5.45,5.41,13.18,8.12,23.19,8.12h114.26c10.01,0,17.74-2.71,23.19-8.12,5.45-5.41,8.18-13.16,8.18-23.25v-98.63c0-1.14-.04-2.2-.12-3.17-.08-.98-.2-1.95-.37-2.93l19.17-19.04c.32,1.46.57,3.01.73,4.64.16,1.63.24,3.3.24,5v114.14c0,16.36-4.39,28.95-13.18,37.78s-21.4,13.25-37.84,13.25H51.03ZM82.76,162.01c-1.14.49-2.18.23-3.11-.79-.94-1.02-1.16-2.05-.67-3.11l10.99-22.95L203.86,21.26l16.72,16.48-114.01,113.89-23.8,10.38ZM229.61,28.83l-16.72-16.85,8.91-8.79c2.03-1.95,4.37-3.01,7.02-3.17,2.64-.16,4.9.69,6.78,2.56l2.81,2.93c2.12,2.04,3.15,4.38,3.11,7.02-.04,2.65-1.08,5.07-3.11,7.26l-8.79,9.03Z"/></svg>`;

const COPY = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2.5"/><path d="M6 15H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v1"/></svg>`;

interface NameEditorOpts {
  address: string;
  code?: string | null; // canonical or formatted; shown grouped
  onDone?: (label: string) => void; // called after save/remove with the new display label
  /** Called when the editor is dismissed without saving or removing. */
  onClose?: () => void;
}

/**
 * A self-contained bottom-sheet editor for naming a contact. Independent of the
 * home view's sheet system so it can be opened from anywhere (Recents, Invite).
 * Shows the name field plus the code as a greyed, copyable line — the code is
 * out of the everyday view but reachable for the rare "re-share this wallet".
 */
// v0.70.12 — names are device-only again. A Drop label that arrives at release
// time becomes the nickname if the person has none; if they already have one,
// it waits here as an "also" until the owner settles on a single master name.
const ALSO_KEY = "m.nickAlso";
function readAlso(): Record<string, string[]> {
  try {
    return JSON.parse(localStorage.getItem(ALSO_KEY) ?? "{}") as Record<string, string[]>;
  } catch {
    return {};
  }
}
export function alsoNames(address: string): string[] {
  return readAlso()[address.toLowerCase()] ?? [];
}
export function addAlso(address: string, label: string): void {
  const k = address.toLowerCase();
  const all = readAlso();
  const cur = all[k] ?? [];
  const nick = readNick(address);
  if (nick && nick.toLowerCase() === label.toLowerCase()) return;
  if (!cur.some((x) => x.toLowerCase() === label.toLowerCase())) cur.push(label);
  all[k] = cur;
  try {
    localStorage.setItem(ALSO_KEY, JSON.stringify(all));
  } catch {
    /* storage blocked */
  }
}
export function clearAlso(address: string): void {
  const all = readAlso();
  delete all[address.toLowerCase()];
  try {
    localStorage.setItem(ALSO_KEY, JSON.stringify(all));
  } catch {
    /* storage blocked */
  }
}
/**
 * Record a Drop label for a person. v1.0.6 — the NEWEST name wins (Alexander,
 * 28 Sep): the sender may have a reason for the new one. The previous
 * nickname is kept as an "also" (still searchable, and Contacts flags the
 * person as carrying two of your names).
 */
export function noteDropLabel(address: string, label: string): void {
  const l = label.replace(/\s+/g, " ").trim();
  if (!l) return;
  const prev = readNick(address);
  if (prev && prev.toLowerCase() === l.toLowerCase()) return;
  setNick(address, l);
  if (prev) addAlso(address, prev); // after setNick: addAlso skips the current nickname
  // the new nickname is no longer an "also"
  const k = address.toLowerCase();
  const all = readAlso();
  if (all[k]) {
    all[k] = all[k].filter((x) => x.toLowerCase() !== l.toLowerCase());
    try {
      localStorage.setItem(ALSO_KEY, JSON.stringify(all));
    } catch {
      /* storage blocked */
    }
  }
}

export function openNameEditor(opts: NameEditorOpts): void {
  const existing = readNick(opts.address);
  const grouped = opts.code ? formatCode(opts.code) : "";

  const scrim = document.createElement("div");
  scrim.className = "nick-scrim";
  scrim.innerHTML = `
    <div class="nick-sheet" role="dialog" aria-modal="true" aria-label="Give this contact a nickname">
      <div class="nick-grab"></div>
      <button class="nick-x" type="button" aria-label="Close">✕</button>
      <p class="nick-title">Give this contact a nickname</p>
      <input class="nick-input" type="text" placeholder="e.g. Mum" maxlength="40"
             autocomplete="off" autocapitalize="words" value="${(existing ?? "").replace(/"/g, "&quot;")}" />
      <p class="nick-err" hidden></p>
      ${
        grouped
          ? `<button class="nick-code" type="button" aria-label="Copy code">
               <span class="nick-code-txt">${grouped}</span>
               <span class="nick-copy">${COPY}</span>
             </button>`
          : ""
      }
      <p class="nick-note">Only you see this name. It replaces their own name everywhere this person appears.</p>
      <button class="nick-save" type="button">Save</button>
      ${existing ? `<button class="nick-remove" type="button">Remove name</button>` : ""}
    </div>`;
  document.body.appendChild(scrim);

  const sheet = scrim.querySelector<HTMLElement>(".nick-sheet")!;
  const input = scrim.querySelector<HTMLInputElement>(".nick-input")!;
  const err = scrim.querySelector<HTMLElement>(".nick-err")!;
  requestAnimationFrame(() => scrim.classList.add("up"));
  setTimeout(() => input.focus(), 60);

  let finished = false;
  function close() {
    scrim.classList.remove("up");
    setTimeout(() => scrim.remove(), 180);
    if (!finished) {
      finished = true;
      opts.onClose?.(); // v0.70.11 — ✕ / scrim: return to where the editor was opened from
    }
  }
  function done() {
    finished = true;
    opts.onDone?.(contactLabel(opts.address, opts.code));
    close();
  }
  function trySave() {
    const val = input.value.trim();
    const clash = nameClash(val, opts.address);
    if (clash) {
      err.textContent = `You already have a contact named "${clash}". Pick a different name.`;
      err.hidden = false;
      input.focus();
      return;
    }
    setNick(opts.address, val);
    clearAlso(opts.address); // one master name
    done();
  }

  input.addEventListener("input", () => {
    err.hidden = true;
  });
  scrim.addEventListener("click", (e) => {
    if (e.target === scrim) close();
  });
  sheet.querySelector(".nick-x")!.addEventListener("click", close);
  sheet.querySelector(".nick-save")!.addEventListener("click", trySave);
  sheet.querySelector(".nick-remove")?.addEventListener("click", () => {
    removeNick(opts.address);
    clearAlso(opts.address);
    done();
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") trySave();
  });

  const codeBtn = sheet.querySelector<HTMLButtonElement>(".nick-code");
  codeBtn?.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(grouped);
      const t = codeBtn.querySelector<HTMLElement>(".nick-code-txt");
      if (t) {
        const prev = t.textContent;
        t.textContent = "Copied";
        setTimeout(() => {
          if (t) t.textContent = prev;
        }, 1100);
      }
    } catch {
      /* clipboard blocked — no-op */
    }
  });
}
